import React, { useState } from 'react';
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { api } from '../../lib/api.js';
import { Field, Text, ObjectList } from './fields.jsx';
import { Empty } from '../../components/states.jsx';
import { formatDate } from '../../lib/date.js';

const blank = () => ({ id: null, number: '', title: '', date: '', assignmentNumber: '', materials: [], links: [] });

const toForm = (lecture) => ({
  id: lecture.id,
  number: String(lecture.number ?? ''),
  title: lecture.title,
  date: lecture.date || '',
  assignmentNumber: lecture.assignmentNumber == null ? '' : String(lecture.assignmentNumber),
  materials: lecture.materials || [],
  links: lecture.links || [],
});

export default function LecturesAdmin({ year, files, notify, reload, reloadFiles }) {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!year) return null;
  const lectures = year.lectures || [];
  const set = (patch) => setDraft((current) => ({ ...current, ...patch }));

  const save = async (event) => {
    event.preventDefault();
    if (!draft.title.trim()) return notify('Vyplňte název hodiny.', 'error');

    setBusy(true);
    const payload = {
      yearId: year.id,
      number: draft.number === '' ? undefined : Number(draft.number),
      title: draft.title,
      date: draft.date || null,
      assignmentNumber: draft.assignmentNumber === '' ? null : Number(draft.assignmentNumber),
      // prázdné řádky by se jinak zobrazily jako mrtvé odkazy
      materials: draft.materials.filter((m) => m.url?.trim()),
      links: draft.links.filter((l) => l.url?.trim() && l.label?.trim()),
    };

    try {
      if (draft.id) await api.admin.updateLecture(draft.id, payload);
      else await api.admin.createLecture(payload);
      await reload();
      setDraft(null);
      notify(draft.id ? 'Hodina uložena.' : 'Hodina přidána.');
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (lecture) => {
    if (!window.confirm(`Opravdu smazat hodinu „${lecture.title}"?`)) return;
    try {
      await api.admin.deleteLecture(lecture.id);
      await reload();
      notify('Hodina smazána.');
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  const move = async (index, delta) => {
    const next = [...lectures];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    try {
      await api.admin.reorder('lectures', next.map((l) => l.id));
      await reload();
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  return (
    <>
      <div className="admin-title">
        <div>
          <h1>Hodiny</h1>
          <p>Ročník {year.label} · {lectures.length} hodin</p>
        </div>
        {!draft && (
          <button type="button" className="btn btn-primary" onClick={() => setDraft(blank())}>
            <Plus size={16} /> Přidat hodinu
          </button>
        )}
      </div>

      {draft && (
        <form className="editor" onSubmit={save} style={{ marginBottom: '1.5rem' }}>
          <h2>{draft.id ? 'Úprava hodiny' : 'Nová hodina'}</h2>

          <Field label="Název" id="nazev-hodiny">
            <Text id="nazev-hodiny" value={draft.title} onChange={(v) => set({ title: v })} placeholder="Např. Úvod do algoritmizace" autoFocus />
          </Field>

          <div className="field-row">
            <Field label="Číslo hodiny" id="cislo-hodiny" hint="Prázdné = další v pořadí">
              <Text id="cislo-hodiny" type="number" min="1" value={draft.number} onChange={(v) => set({ number: v })} />
            </Field>
            <Field label="Datum" id="datum-hodiny">
              <Text id="datum-hodiny" type="date" value={draft.date} onChange={(v) => set({ date: v })} />
            </Field>
            <Field label="Zadaný úkol č." id="ukol-hodiny" hint="Propojí hodinu se zadáním">
              <Text id="ukol-hodiny" type="number" min="1" value={draft.assignmentNumber} onChange={(v) => set({ assignmentNumber: v })} />
            </Field>
          </div>

          <Field
            label="Materiály"
            hint="Slajdy, záznam a fotky. Bez popisku se ukáže název typu. Sponka vybírá z nahraných souborů, šipka nahraje nový rovnou odsud — záznam i fotka se pak ukážou přímo na webu."
          >
            <ObjectList
              value={draft.materials}
              onChange={(materials) => set({ materials })}
              empty={{ kind: 'slides', label: '', url: '' }}
              addLabel="Přidat materiál"
              files={files}
              onNeedFiles={reloadFiles}
              notify={notify}
              fields={[
                { key: 'kind', label: 'Typ', type: 'select', options: [
                  { value: 'slides', label: 'Slajdy' },
                  { value: 'recording', label: 'Záznam' },
                  { value: 'photo', label: 'Fotka' },
                ] },
                { key: 'label', label: 'Popisek (nepovinný)' },
                { key: 'url', label: 'Odkaz nebo soubor', pickable: true },
              ]}
            />
          </Field>

          <Field label="Doplňkové odkazy" hint="Animace, ukázkové skripty, vizualizace…">
            <ObjectList
              value={draft.links}
              onChange={(links) => set({ links })}
              empty={{ label: '', url: '' }}
              addLabel="Přidat odkaz"
              files={files}
              onNeedFiles={reloadFiles}
              notify={notify}
              fields={[
                { key: 'label', label: 'Popisek' },
                { key: 'url', label: 'Odkaz nebo soubor', pickable: true },
              ]}
            />
          </Field>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Ukládám…' : 'Uložit'}</button>
            <button type="button" className="btn btn-ghost" onClick={() => setDraft(null)}>Zrušit</button>
          </div>
        </form>
      )}

      {lectures.length === 0 && !draft ? (
        <Empty title="Zatím žádné hodiny">Přidejte první hodinu tlačítkem nahoře.</Empty>
      ) : (
        <div className="stack-tight">
          {lectures.map((lecture, index) => (
            <div className="admin-row" key={lecture.id}>
              <div className="admin-row-order">
                <button type="button" className="icon-btn" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Posunout nahoru">
                  <ChevronUp size={13} />
                </button>
                <button type="button" className="icon-btn" onClick={() => move(index, 1)} disabled={index === lectures.length - 1} aria-label="Posunout dolů">
                  <ChevronDown size={13} />
                </button>
              </div>

              <div className="admin-row-main">
                <div className="admin-row-title">{lecture.number}. {lecture.title}</div>
                <div className="admin-row-sub">
                  {lecture.date && <span>{formatDate(lecture.date)}</span>}
                  <span>{lecture.materials?.length || 0} materiálů</span>
                  {lecture.links?.length > 0 && <span>{lecture.links.length} odkazů</span>}
                  {lecture.assignmentNumber != null && <span>úkol {lecture.assignmentNumber}</span>}
                </div>
              </div>

              <div className="admin-row-actions">
                <button type="button" className="icon-btn" onClick={() => setDraft(toForm(lecture))} aria-label={`Upravit ${lecture.title}`}>
                  <Pencil size={15} />
                </button>
                <button type="button" className="icon-btn icon-btn-danger" onClick={() => remove(lecture)} aria-label={`Smazat ${lecture.title}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
